import { useEffect } from "react";
import { Lock, LogOut, RefreshCw, ShieldCheck } from "lucide-react";
import { useAuth } from "@/auth/AuthContext";
import { BrandMark, useBrandTheme } from "@/components/Brand";
import Training from "@/components/Training";

/** What a staff login sees until its required training is done (or while an admin has paused its access): their courses and nothing else.
 *  The server refuses every other request from a locked login; this page is just the friendly side of that. When the last required course is
 *  finished the lock clears and the full system opens by itself. */
export default function TrainingPortal() {
  const { user, logout, refresh } = useAuth();
  useBrandTheme(user.branding);
  const lock = user.trainingLock || { locked: true, reason: "", missing: [] };
  const manual = lock.reason === "manual";
  // check again every so often and when the tab comes back into view: an admin may have unlocked this login, or a manager approved some evidence
  useEffect(() => {
    const again = () => { if (document.visibilityState === "visible") refresh(); };
    const t = setInterval(again, 30000);
    document.addEventListener("visibilitychange", again);
    return () => { clearInterval(t); document.removeEventListener("visibilitychange", again); };
  }, [refresh]);
  return (
    <div className="App grain min-h-screen bg-[var(--hp-bg)]" data-testid="training-portal">
      <header className="border-b border-[var(--hp-border)] bg-[var(--hp-sidebar)]">
        <div className="mx-auto flex max-w-5xl flex-wrap items-center gap-3 px-4 py-4 sm:px-6">
          <BrandMark branding={user.branding} size="sm" />
          <div className="ml-auto flex items-center gap-3">
            <span className="hidden text-xs text-[var(--hp-muted)] sm:inline" data-testid="portal-user">{user.name}</span>
            <button data-testid="portal-check" onClick={refresh} className="inline-flex items-center gap-1.5 rounded-full border border-[var(--hp-border-hi)] px-3 py-1.5 text-xs font-semibold text-[var(--hp-heading)] hover:bg-[var(--hp-tint-5)]"><RefreshCw className="h-3.5 w-3.5" /> Check again</button>
            <button data-testid="portal-signout" onClick={logout} className="inline-flex items-center gap-1.5 rounded-full border border-[var(--hp-border-hi)] px-3 py-1.5 text-xs font-semibold text-[var(--hp-heading)] hover:bg-[var(--hp-tint-5)]"><LogOut className="h-3.5 w-3.5" /> Sign out</button>
          </div>
        </div>
      </header>
      <main className="mx-auto max-w-5xl space-y-5 px-4 py-6 sm:px-6">
        <div className="flex items-start gap-3 rounded-2xl border border-[var(--hp-primary)]/40 bg-[var(--hp-primary)]/8 p-4" data-testid="portal-banner">
          {manual ? <Lock className="mt-0.5 h-5 w-5 shrink-0 text-[var(--hp-profit)]" /> : <ShieldCheck className="mt-0.5 h-5 w-5 shrink-0 text-[var(--hp-profit)]" />}
          <div className="min-w-0 text-sm text-[var(--hp-text)]">
            {manual ? (
              <p><b className="text-[var(--hp-heading)]">Your access has been paused.</b> Your training is still open below. Ask your manager when the rest of the system will be back.</p>
            ) : (
              <>
                <p><b className="text-[var(--hp-heading)]">Welcome{user.name ? `, ${user.name.split(" ")[0]}` : ""}.</b> Finish the training below to open the rest of the system.</p>
                {lock.missing?.length > 0 && (
                  <p className="mt-1 text-xs text-[var(--hp-muted)]" data-testid="portal-missing">Still to do: {lock.missing.map((m) => m.title).join(", ")}.</p>
                )}
              </>
            )}
          </div>
        </div>
        <Training api={`${process.env.REACT_APP_BACKEND_URL}/api`} staff={[]} onChanged={refresh} />
      </main>
    </div>
  );
}
